import { Suspense, lazy, useState } from "react";
import { useTranslation } from "react-i18next";
import { Smartphone } from "lucide-react";

import { Button } from "@/components/ui/button";

/**
 * The QR and the wait are fetched only when somebody opens them. Listening
 * needs the room's channel code, and the landing must not download a room
 * before it paints — `tests/perf/bundle.perf.test.ts` holds that.
 */
const PairingDialog = lazy(async () => ({
  default: (await import("./PairingDialog")).PairingDialog,
}));

/**
 * The way in, for an invitation that arrived on a phone.
 *
 * The computer draws a QR, the phone's own camera opens it, and the room the
 * phone chooses comes back here and is walked into like a typed code. Nothing
 * is installed and nothing is scanned by the page: every phone camera already
 * reads a QR and opens the address in it.
 */
export function JoinFromPhone({
  onJoin,
  disabled,
}: {
  onJoin: (code: string) => void;
  disabled?: boolean | undefined;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* A computer's offer: a phone has its own, at the room's threshold. */}
      <Button
        type="button"
        variant="outline"
        data-testid="entry-join-from-phone"
        disabled={disabled}
        onClick={() => setOpen(true)}
        className="hidden w-full sm:inline-flex"
      >
        <Smartphone className="mr-2 h-4 w-4" aria-hidden />
        {t("pairing.joinFromPhone")}
      </Button>
      {open && (
        <Suspense fallback={null}>
          <PairingDialog onJoin={onJoin} onClose={() => setOpen(false)} />
        </Suspense>
      )}
    </>
  );
}
